import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { toggleFilterDisplay, updateUriRef } from '../../../actions/filterActions.ts';
import { createNewFilterUriRef } from '../../../utils/uriUtils.ts';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants.ts';
import { QP_SEARCH_QUERY } from '../../../constants/queryParamConstants.ts';
import { SbSharedRefineMenuSearchInput } from '../SbSharedRefineMenuSearchInput/SbSharedRefineMenuSearchInput.tsx';
import { FILTER_REFINE_SEARCH } from '../sbSharedRefineMenuConstants.ts';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { type TrackEventEventArgument } from '../../../utils/tracking/searchBrowse/filterTracking.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type SbSharedRefineMenuSearchWithin_itemSearch$key } from './__generated__/SbSharedRefineMenuSearchWithin_itemSearch.graphql.ts';

const messages = defineMessages({
    placeholder: {
        id: 'sb.SbSharedRefineMenu.searchWithin.placeholder',
        defaultMessage: `Search within {totalResultsCount, plural, one {# result} other {# results}}`,
    },
    placeholderNoCount: {
        id: 'sb.SbSharedRefineMenu.searchWithin.placeholderNoCount',
        defaultMessage: `Search within results`,
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuSearchWithin_itemSearch on ItemSearchQueryConnection {
        pageType
        totalResults
        appliedFilters {
            name
            values {
                urlLabel
            }
        }
    }
`;

type Props = {
    horizontalSpacing?: 'small' | 'medium';
    hideResultsCount?: boolean;
    itemSearch: SbSharedRefineMenuSearchWithin_itemSearch$key;
    onSubmit: (event?: TrackEventEventArgument) => void;
    size?: 'medium' | 'large';
};

export const SbSharedRefineMenuSearchWithin: FC<Props> = ({
    horizontalSpacing = 'medium',
    hideResultsCount = false,
    itemSearch: itemSearchRef,
    onSubmit,
    size = 'medium',
}) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { isDesktop } = useServerVarsContext();
    const { filtersShown, generatedUriRef, refetchInFlight } = useSbSelector(
        state => state.filters
    );
    const { totalResults, pageType } = itemSearch;

    const [inputValue, setInputValue] = useState(() => {
        const appliedSearchFilter = itemSearch.appliedFilters?.find(
            filter => filter?.name === FILTER_REFINE_SEARCH
        );
        return appliedSearchFilter?.values?.[0]?.urlLabel || '';
    });

    const prevFiltersShownRef = useRef(filtersShown);
    const handleSearchSubmitRef = useRef(() => {});

    const updateFilters = useCallback(
        (value: string, uriRef?: string | null, event?: TrackEventEventArgument) => {
            const newUriRef = createNewFilterUriRef(uriRef ?? '', QP_SEARCH_QUERY, value);

            dispatch(
                updateUriRef({
                    filterName: FILTER_REFINE_SEARCH,
                    filterValue: { urlLabel: value },
                    uriRef: newUriRef,
                    event,
                })
            );
            dispatch(toggleFilterDisplay(false));
        },
        [dispatch]
    );

    const handleSearchSubmit = useCallback(
        (event?: TrackEventEventArgument) => {
            if (inputValue === '' || refetchInFlight) {
                return;
            }
            // apply filters with new value
            updateFilters(inputValue, generatedUriRef, event);
            setInputValue('');
            onSubmit(event);
        },
        [generatedUriRef, inputValue, onSubmit, refetchInFlight, updateFilters]
    );
    handleSearchSubmitRef.current = handleSearchSubmit;

    useEffect(() => {
        if (prevFiltersShownRef.current && !filtersShown) {
            handleSearchSubmitRef.current();
        }
        prevFiltersShownRef.current = filtersShown;
    }, [filtersShown]);

    const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    }, []);

    const handleSubmit = useCallback(
        (event: TrackEventEventArgument) => {
            event?.preventDefault();
            handleSearchSubmit(event);
        },
        [handleSearchSubmit]
    );

    const placeholder = intl.formatMessage(
        hideResultsCount ? messages.placeholderNoCount : messages.placeholder,
        { totalResultsCount: totalResults ?? 0 }
    );

    if (pageType === pageTypes.SEARCH) {
        return null;
    }

    return (
        <SbSharedRefineMenuSearchInput
            autoComplete="on"
            className={classnames(
                dibsCss.relative, // position relative keeps input caret at the place when scrolling, iOS - safari/chrome bug fix.
                dibsCss.bgWhite,
                isDesktop && [
                    dibsCss.mb0,
                    dibsCss.pbSmall,
                    dibsCss.borderB,
                    dibsCss.borderBSolid,
                    dibsCss.borderBDolphin,
                ]
            )}
            horizontalSpacing={horizontalSpacing}
            onInput={handleInput}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            size={size}
            value={inputValue}
            disabled={refetchInFlight}
        />
    );
};
